package com.ijoin.ui.components

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.AccountBalance
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.OpenInNew
import androidx.compose.material.icons.filled.Refresh
import androidx.compose.material.icons.filled.Settings
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.ijoin.data.model.ConnectAccountStatus
import com.ijoin.i18n.Strings
import com.ijoin.ui.design.EventCardAccent
import com.ijoin.ui.design.EventCardAccentDark
import com.ijoin.ui.design.EventCardAccentSoft
import com.ijoin.ui.design.EventCardOutline
import com.ijoin.ui.design.EventCardWarm
import com.ijoin.ui.design.EventCardWarmSoft
import com.ijoin.ui.design.EventOutlineButton
import com.ijoin.ui.design.EventPill
import com.ijoin.ui.design.EventPrimaryButton
import com.ijoin.ui.design.EventSurfaceCard

@Composable
fun ConnectStatusCard(
    status: ConnectAccountStatus?,
    isLoading: Boolean,
    isSwitchingMode: Boolean,
    strings: Strings,
    onStartOnboarding: () -> Unit,
    onEnableDirectPayments: () -> Unit,
    onOpenDashboard: () -> Unit,
    onSwitchToPlatform: () -> Unit,
    modifier: Modifier = Modifier
) {
    EventSurfaceCard(
        modifier = modifier.fillMaxWidth(),
        backgroundColor = EventCardAccentSoft,
        borderColor = EventCardOutline,
        contentPadding = PaddingValues(20.dp)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                Icon(
                    imageVector = Icons.Default.AccountBalance,
                    contentDescription = null,
                    tint = EventCardAccentDark,
                    modifier = Modifier.size(24.dp)
                )
                Text(
                    text = strings.connectTitle,
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.SemiBold,
                    color = EventCardAccentDark
                )
            }
            if (status != null) {
                ConnectStatusPill(status = status, strings = strings)
            }
        }

        if (isLoading && status == null) {
            Row(
                modifier = Modifier.fillMaxWidth().padding(vertical = 8.dp),
                horizontalArrangement = Arrangement.Center
            ) {
                CircularProgressIndicator(
                    modifier = Modifier.size(24.dp),
                    strokeWidth = 2.dp,
                    color = EventCardAccent
                )
            }
        } else if (status != null) {
            ConnectStatusContent(
                status = status,
                isLoading = isLoading,
                isSwitchingMode = isSwitchingMode,
                strings = strings,
                onStartOnboarding = onStartOnboarding,
                onEnableDirectPayments = onEnableDirectPayments,
                onOpenDashboard = onOpenDashboard,
                onSwitchToPlatform = onSwitchToPlatform
            )
        }
    }
}

@Composable
private fun ConnectStatusPill(
    status: ConnectAccountStatus,
    strings: Strings
) {
    val (label, background, textColor) = when {
        status.isDirectMode && status.chargesEnabled == true -> Triple(
            strings.connectStatusActive,
            EventCardAccentSoft,
            EventCardAccent
        )
        status.isDirectMode -> Triple(
            strings.connectStatusPending,
            EventCardWarmSoft,
            EventCardWarm
        )
        status.isUnderReview -> Triple(
            strings.connectStatusUnderReview,
            EventCardWarmSoft,
            EventCardWarm
        )
        status.needsOnboarding -> Triple(
            strings.connectStatusSetupNeeded,
            EventCardWarmSoft,
            EventCardWarm
        )
        else -> Triple(
            strings.connectStatusPlatform,
            EventCardAccentSoft,
            EventCardAccentDark
        )
    }

    EventPill(
        text = label,
        backgroundColor = background,
        textColor = textColor,
        borderColor = EventCardOutline
    )
}

@Composable
private fun ConnectStatusContent(
    status: ConnectAccountStatus,
    isLoading: Boolean,
    isSwitchingMode: Boolean,
    strings: Strings,
    onStartOnboarding: () -> Unit,
    onEnableDirectPayments: () -> Unit,
    onOpenDashboard: () -> Unit,
    onSwitchToPlatform: () -> Unit
) {
    Column(
        verticalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        // Status description
        val description = when {
            status.isDirectMode && status.chargesEnabled == true -> strings.connectActiveDescription
            status.isUnderReview -> strings.connectUnderReviewDescription
            status.needsOnboarding -> strings.connectOnboardingDescription
            else -> strings.connectPlatformDescription
        }
        Text(
            text = description,
            style = MaterialTheme.typography.bodyMedium,
            color = EventCardAccentDark.copy(alpha = 0.7f)
        )

        // Platform fee (only shown in direct mode)
        if (status.isDirectMode && status.platformFeePercent != null) {
            Row(
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = "${strings.connectPlatformFeeLabel}:",
                    style = MaterialTheme.typography.bodySmall,
                    color = EventCardAccentDark.copy(alpha = 0.7f)
                )
                Text(
                    text = strings.connectPlatformFeeValue((status.platformFeePercent * 100).toInt()),
                    style = MaterialTheme.typography.bodyMedium,
                    fontWeight = FontWeight.SemiBold,
                    color = EventCardAccentDark
                )
            }
        }

        // Action buttons
        ConnectActionButtons(
            status = status,
            isLoading = isLoading,
            isSwitchingMode = isSwitchingMode,
            strings = strings,
            onStartOnboarding = onStartOnboarding,
            onEnableDirectPayments = onEnableDirectPayments,
            onOpenDashboard = onOpenDashboard,
            onSwitchToPlatform = onSwitchToPlatform
        )
    }
}

@Composable
private fun ConnectActionButtons(
    status: ConnectAccountStatus,
    isLoading: Boolean,
    isSwitchingMode: Boolean,
    strings: Strings,
    onStartOnboarding: () -> Unit,
    onEnableDirectPayments: () -> Unit,
    onOpenDashboard: () -> Unit,
    onSwitchToPlatform: () -> Unit
) {
    when {
        // Direct mode - show dashboard and switch to platform
        status.isDirectMode && status.chargesEnabled == true -> {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                EventPrimaryButton(
                    text = strings.connectOpenDashboard,
                    onClick = onOpenDashboard,
                    leadingIcon = Icons.Default.OpenInNew,
                    modifier = Modifier.weight(1f)
                )
            }
            EventOutlineButton(
                text = strings.connectSwitchToPlatform,
                onClick = onSwitchToPlatform,
                isLoading = isSwitchingMode,
                modifier = Modifier.fillMaxWidth()
            )
        }

        // Direct mode but pending/under review
        status.isDirectMode -> {
            EventOutlineButton(
                text = strings.connectSwitchToPlatform,
                onClick = onSwitchToPlatform,
                isLoading = isSwitchingMode,
                modifier = Modifier.fillMaxWidth()
            )
        }

        // Platform mode - no Connect account yet
        !status.hasConnectAccount -> {
            EventPrimaryButton(
                text = strings.connectEnableDirectAction,
                onClick = onStartOnboarding,
                isLoading = isLoading,
                leadingIcon = Icons.Default.Settings,
                modifier = Modifier.fillMaxWidth()
            )
        }

        // Platform mode - onboarding incomplete
        status.needsOnboarding -> {
            EventPrimaryButton(
                text = strings.connectContinueOnboarding,
                onClick = onStartOnboarding,
                isLoading = isLoading,
                leadingIcon = Icons.Default.Refresh,
                modifier = Modifier.fillMaxWidth()
            )
        }

        // Platform mode - under review
        status.isUnderReview -> {
            Text(
                text = strings.connectUnderReviewDescription,
                style = MaterialTheme.typography.bodySmall,
                color = EventCardWarm
            )
        }

        // Platform mode - ready to enable direct payments
        status.canEnableDirectPayments == true -> {
            EventPrimaryButton(
                text = strings.connectEnableDirectAction,
                onClick = onEnableDirectPayments,
                isLoading = isSwitchingMode,
                leadingIcon = Icons.Default.Check,
                modifier = Modifier.fillMaxWidth()
            )
        }
    }
}
